/**
 * Runtime configuration, fetched at boot instead of baked into the bundle.
 *
 * Why this exists: Vite inlines `import.meta.env.VITE_*` at build time, but the
 * CloudOS front-end pipeline builds one artifact and deploys that same artifact
 * to every environment. So anything that must differ between staging and
 * production — the APS tier and its client id above all — cannot come from
 * `import.meta.env`, or staging would authenticate against production APS.
 *
 * CloudOS's answer is the `environment` block in
 * `.cloudos/service_config/<zone>/<env>/<region>.yml`, whose values are
 * environment-specific by construction and are written into the bucket at
 * deploy time.
 *
 * Local development needs none of this: the fetch 404s, we fall back to
 * `import.meta.env`, and `.env` keeps working exactly as before. (CloudOS
 * suggests serving an `environment.local.json` in dev instead; the fallback
 * makes that unnecessary here, and keeps one source of truth for local config.)
 */

/**
 * Confirmed against CloudOS's own documentation, "Application Environment
 * Management - Front-End" (Confluence FPS/498075466): the `environment` block
 * in service_config "will be rendered at deploy time into the
 * **environment.json** file and stored under your S3 folder", and the
 * documented way to consume it is exactly the fetch-on-load below.
 */
const CONFIG_URL = "/environment.json";

export type RuntimeConfig = Record<string, string>;

let cache: RuntimeConfig = {};

/**
 * Fetch runtime config once, before the app renders.
 *
 * Deliberately never rejects. A missing or malformed config file must not take
 * the whole app down — it falls back to build-time values, which is exactly
 * what local dev and any pre-`environment.json` deploy rely on.
 */
export async function loadRuntimeConfig(): Promise<void> {
  try {
    const res = await fetch(CONFIG_URL, { cache: "no-store" });
    if (!res.ok) return;
    const parsed: unknown = await res.json();
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      cache = Object.fromEntries(
        Object.entries(parsed as Record<string, unknown>)
          .filter(([, v]) => typeof v === "string")
          .map(([k, v]) => [k, v as string])
      );
    }
  } catch {
    // Offline, 404, or invalid JSON — fall back to build-time values.
  }
}

/**
 * Read a config value, preferring the deployed environment's value over the
 * one baked in at build time.
 */
export function getConfigValue(key: string): string | undefined {
  const runtime = cache[key];
  if (runtime !== undefined && runtime !== "") return runtime;
  const buildTime = (import.meta.env as Record<string, unknown>)[key];
  return typeof buildTime === "string" && buildTime !== ""
    ? buildTime
    : undefined;
}
